'use client';
import styled from 'styled-components';

import { breakpoints } from '@styles/breakpoint';
import { Spacers } from '@styles/theme';

import { PageBottom as BasePageBottom } from '@components/Common';

interface TextareaFieldProps {
  $hasError?: boolean;
}

export const TextareaContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const TextareaField = styled.textarea<TextareaFieldProps>`
  padding: ${Spacers.SM}rem ${Spacers.BASE}rem;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-family: inherit;
  width: 100%;
  min-height: 5.625rem;
  background-color: ${({ theme }) => theme.colors.backgroundAccent};
  border: ${(props) => (props.$hasError ? `1px solid ${props.theme.colors.statusError}` : 'none')};
  border-radius: ${Spacers.XS}rem;
  outline: none;
  resize: vertical;
  color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyBodyLight};
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;

  &::placeholder {
    color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyBodyLight};
    position: absolute;
    top: ${Spacers.SM}rem;
    left: ${Spacers.BASE}rem;
    pointer-events: none;
    transition: all 0.2s ease;
  }

  &:focus {
    color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyHeadersLight};

    &::placeholder {
      color: transparent;
    }
  }

  &:not(:placeholder-shown) {
    &::placeholder {
      color: transparent;
    }
  }
`;

// Mobile Button Style (shows on mobile instead of textarea)
export const MobileTextareaButton = styled.button<TextareaFieldProps>`
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-family: inherit;
  width: 100%;
  min-height: 5.625rem;
  background-color: ${({ theme }) => theme.colors.backgroundAccent};
  border: ${(props) => (props.$hasError ? `1px solid ${props.theme.colors.statusError}` : 'none')};
  border-radius: ${Spacers.XS}rem;
  outline: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyBodyLight};
  line-height: 1.5;
  padding: 0;

  &:focus {
    color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyHeadersLight};
  }
`;

export const MobileTextContent = styled.div`
  padding: ${Spacers.SM}rem ${Spacers.BASE}rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  width: 100%;
`;

// Full Screen Modal Styles
export const FullScreenContainer = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  ${breakpoints.mobile} {
    align-items: stretch;
    justify-content: stretch;
  }
`;

export const FullScreenWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: 500px;
  max-height: 600px;
  border-radius: ${Spacers.XS}rem;

  ${breakpoints.mobile} {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`;

export const FullScreenHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacers.BASE}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.backgroundAccent};
`;

export const HeaderTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.heading1.fontSize};
  color: ${({ theme }) => theme.colors.typographyHeadersLight};
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacers.XS}rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.typographyBodyLight};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${({ theme }) => theme.colors.typographyHeadersLight};
  }
`;

export const FullScreenTextareaField = styled.textarea`
  flex: 1;
  padding: ${Spacers.BASE}rem;
  border: none;
  outline: none;
  resize: none;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.typographyBodyLight};
  background-color: transparent;
  line-height: 1.6;
  min-height: 200px; /* Minimum usable height */
  -webkit-overflow-scrolling: touch; /* Enables smooth scrolling behavior on iOS,important for keyboard interactions */
  scroll-behavior: auto;
  overscroll-behavior: contain;

  &::placeholder {
    color: ${({ theme }) => theme.colors.typographyBodyLight};
    opacity: 0.7;
  }

  &:focus {
    color: ${({ theme }) => theme.colors.typographyHeadersLight};
  }
`;

/* Modal-specific variant of PageBottom that participates in flex layout */
export const ModalPageBottom = styled(BasePageBottom)`
  /* Fixed over content; offset is provided inline via style bottom: keyboardInset */
  position: fixed;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 10000;
  /* Prevent rubber-band/scroll on iOS when dragging the bar */
  touch-action: none;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
  pointer-events: auto;
`;
